<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import DataTable from '@/components/DataTable.vue'
  import BookmarkInput from '@/components/BookmarkInput.vue'
  import { ref, inject, onMounted, computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useSubsidyStore } from '@/stores/subsidyStore'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const subsidyStore = useSubsidyStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '助成金' }]

  // リファレンス変数
  const showPopupEdit = ref(false)
  const isEdit = ref(false)

  const searchText = ref('')
  const searchKey = 'status'
  const searchSelect = ref('')
  const searchList = computed(() => subsidyStore.statusOptions)
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')
  const searchColor = ref('')
  const searchSelectDateTiming = ref('')
  const searchSelectDateTimingList = ref(['受付中', '期限間近', '超過'])

  // 統一されたgettersを使用したcomputed
  const companyOptions = computed(() => {
    return subsidyStore.companyNameOptions || []
  })

  const branchOptions = computed(() => {
    return subsidyStore.branchNameOptions || []
  })

  const staffOptions = computed(() => {
    return subsidyStore.staffNameOptions || []
  })

  // 会社・事業場・従業員セレクト状態
  const companySelectState = computed(() => subsidyStore.getCompanySelectState(isEdit.value))
  const branchSelectState = computed(() => subsidyStore.getBranchSelectState(isEdit.value))
  const staffSelectState = computed(() => subsidyStore.getStaffSelectState(isEdit.value))

  // 受付状況フィルターのみを処理
  const dateTimingFilteredData = computed(() => {
    if (!searchSelectDateTiming.value) {
      return subsidyStore.subsidies // フィルター無しの場合は元データ
    }

    return subsidyStore.subsidies.filter(item => {
      const now = new Date()
      now.setHours(0, 0, 0, 0)

      // 支給申請期限を取得
      const endDate = item.paymentApplicationDeadline ? new Date(item.paymentApplicationDeadline) : null

      // 期限日が設定されていない場合は除外
      if (!endDate) {
        return false
      }

      endDate.setHours(0, 0, 0, 0)

      switch (searchSelectDateTiming.value) {
        case '受付中':
          // 現在 <= 終了日
          return now <= endDate
        case '期限間近':
          // 現在 <= 終了日 かつ 終了日まで14日以内
          const twoWeeksBeforeEndDate = new Date(endDate)
          twoWeeksBeforeEndDate.setDate(endDate.getDate() - 14)
          twoWeeksBeforeEndDate.setHours(0, 0, 0, 0)
          return twoWeeksBeforeEndDate <= now && now <= endDate
        case '超過':
          // 終了日 < 現在
          return endDate < now
        default:
          return true
      }
    })
  })

  // methods /////////////////////////////////////

  onMounted(async () => {
    // ステータスで絞り込み
    if (route.query.status) {
      // ゲッターを使用する場合
      searchSelect.value = subsidyStore.statusLabelToValue(route.query.status)
    }

    // 受付状況で絞り込み
    if (route.query.dateTiming) {
      searchSelectDateTiming.value = route.query.dateTiming
    }

    // テーブル定義を初期化
    subsidyStore.initTableList()

    // 会社名リストを取得
    await subsidyStore.getCompanyNames()
    // 助成金一覧を取得
    await subsidyStore.getSubsidies()

    if (route.query.action === 'new') {
      openEditDialog()
    }
  })

  // 保存
  const onSave = async () => {
    // 保存処理
    const message = await subsidyStore.saveSubsidy()

    if (message) {
      await dialogStore.showMessageDialog('保存', message)
      showPopupEdit.value = false
    }
  }

  // コピー
  const onCopy = () => {
    subsidyStore.copySubsidy()
    isEdit.value = false
  }

  // 削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', 'この助成金データを削除してもよろしいですか？')) {
      const message = await subsidyStore.deleteSubsidy()

      if (message) {
        await dialogStore.showMessageDialog('削除', message)
        showPopupEdit.value = false
      }
    }
  }

  // 編集
  const openEditDialog = async item => {
    isEdit.value = item ? true : false
    await subsidyStore.setFormData(item)
    showPopupEdit.value = true
  }

  // 会社変更時の処理
  const onCompanyChange = async () => {
    subsidyStore.isDeletedCompany = false
    subsidyStore.isDeletedBranch = false
    subsidyStore.isDeletedStaff = false
    await subsidyStore.onCompanyChange()
  }

  // 事業場変更時の処理
  const onBranchChange = async () => {
    subsidyStore.isDeletedBranch = false
    subsidyStore.isDeletedStaff = false
    await subsidyStore.onBranchChange()
  }

  // 従業員変更時の処理（必要なら）
  const onStaffChange = () => {
    subsidyStore.isDeletedStaff = false
  }

  // 日付変更時の処理
  const onDateChange = (type, event) => {
    subsidyStore.updateDateInfo(type, event.target.value)
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>助成金</h2>
      <button type="button" class="new" @click="openEditDialog()">新規登録</button>
      <div class="filters">
        <div class="area">
          <BookmarkInput v-model="searchColor" />
        </div>
        <div class="select">
          状況:
          <select v-model="searchSelect">
            <option value="">-</option>
            <option v-for="item in searchList" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="select">
          受付状況:
          <select v-model="searchSelectDateTiming">
            <option value="">-</option>
            <option v-for="item in searchSelectDateTimingList" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>
        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>
    <div class="main-body">
      <DataTable :table-list="subsidyStore.tableList" :data-list="dateTimingFilteredData" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditDialog" @length="length = $event" :search-key="searchKey" :search-select="searchSelect" :search-list="searchList" :search-color="searchColor" />
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <EditDialog v-model:visible="showPopupEdit" :show-delete-button="isEdit" :show-copy-button="isEdit" medium :save-button-text="isEdit ? '保存' : '登録'" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>{{ isEdit ? '編集' : '新規登録' }}</template>
    <template v-slot:select>
      <div class="select">
        <select v-model="subsidyStore.formData.status">
          <option></option>
          <option v-for="item in subsidyStore.statusOptions" :key="item.value" :value="item.value">
            {{ item.label }}
          </option>
        </select>
      </div>
    </template>
    <template v-slot:content>
      <div class="dialog-edit">
        <div class="dialog-grid wide">
          <dl class="sticky">
            <dt>助成金種類</dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.subsidyType" />
              <input type="text" v-model="subsidyStore.formData.subsidyType" />
            </dd>
            <dt>会社</dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.companyId" />
              <div class="select">
                <select v-model="subsidyStore.formData.companyId" @change="onCompanyChange">
                  <option v-if="companySelectState === 'deleted'" :value="subsidyStore.formData.companyId" disabled>削除された会社</option>
                  <option v-else-if="companySelectState === 'select'" value="null" disabled>会社を選択</option>
                  <option v-else value="null" disabled>会社を選択</option>
                  <option v-for="company in companyOptions" :key="company.value" :value="company.value">
                    {{ company.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>事業場</dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.branchId" />
              <div class="select">
                <select v-model="subsidyStore.formData.branchId" @change="onBranchChange" :disabled="!subsidyStore.formData.companyId">
                  <option v-if="branchSelectState === 'deleted'" :value="subsidyStore.formData.branchId" disabled>削除された事業場</option>
                  <option v-else-if="branchSelectState === 'select'" value="null" disabled>事業場を選択</option>
                  <option v-else value="null" disabled>事業場を選択</option>
                  <option v-for="branch in branchOptions" :key="branch.value" :value="branch.value">
                    {{ branch.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>対象者</dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.staffId" />
              <div class="select">
                <select v-model="subsidyStore.formData.staffId" @change="onStaffChange" :disabled="!subsidyStore.formData.branchId">
                  <!-- 削除された従業員の場合 -->
                  <option v-if="staffSelectState === 'deleted'" :value="subsidyStore.formData.staffId" disabled>削除された従業員</option>
                  <!-- 従業員選択を促す場合 -->
                  <option v-else-if="staffSelectState === 'select'" value="null" disabled>従業員を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>従業員を選択</option>
                  <!-- 選択肢リスト -->
                  <option v-for="staff in staffOptions" :key="staff.value" :value="staff.value">
                    {{ staff.label }}
                  </option>
                </select>
              </div>
            </dd>

            <dt>支給申請期限日</dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.paymentApplicationDeadline" />
              <input type="date" v-model="subsidyStore.formData.paymentApplicationDeadline" />
            </dd>

            <dt>備考</dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.memo" />
              <input type="text" v-model="subsidyStore.formData.memo" />
            </dd>
          </dl>
          <div class="border"></div>
          <dl class="aling-left">
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.applicationStartDate.isChecked" @change="subsidyStore.updateCheckboxState('applicationStartDate')" />
                申請開始日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.applicationStartDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.applicationStartDate.date" @change="onDateChange('applicationStartDate', $event)" :disabled="!subsidyStore.formData.dateJson.applicationStartDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.applicationStartDate?.userName">
                  {{ subsidyStore.getCheckInfo('applicationStartDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.submissionDate.isChecked" @change="subsidyStore.updateCheckboxState('submissionDate')" />
                提出日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.submissionDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.submissionDate.date" @change="onDateChange('submissionDate', $event)" :disabled="!subsidyStore.formData.dateJson.submissionDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.submissionDate?.userName">
                  {{ subsidyStore.getCheckInfo('submissionDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.paymentStartDate.isChecked" @change="subsidyStore.updateCheckboxState('paymentStartDate')" />
                支給開始日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.paymentStartDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.paymentStartDate.date" @change="onDateChange('paymentStartDate', $event)" :disabled="!subsidyStore.formData.dateJson.paymentStartDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.paymentStartDate?.userName">
                  {{ subsidyStore.getCheckInfo('paymentStartDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.salaryIncreaseDate.isChecked" @change="subsidyStore.updateCheckboxState('salaryIncreaseDate')" />
                昇給日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.salaryIncreaseDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.salaryIncreaseDate.date" @change="onDateChange('salaryIncreaseDate', $event)" :disabled="!subsidyStore.formData.dateJson.salaryIncreaseDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.salaryIncreaseDate?.userName">
                  {{ subsidyStore.getCheckInfo('salaryIncreaseDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.joinDate.isChecked" @change="subsidyStore.updateCheckboxState('joinDate')" />
                入社日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.joinDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.joinDate.date" @change="onDateChange('joinDate', $event)" :disabled="!subsidyStore.formData.dateJson.joinDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.joinDate?.userName">
                  {{ subsidyStore.getCheckInfo('joinDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.applicableDate.isChecked" @change="subsidyStore.updateCheckboxState('applicableDate')" />
                該当日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.applicableDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.applicableDate.date" @change="onDateChange('applicableDate', $event)" :disabled="!subsidyStore.formData.dateJson.applicableDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.applicableDate?.userName">
                  {{ subsidyStore.getCheckInfo('applicableDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.planStartDate.isChecked" @change="subsidyStore.updateCheckboxState('planStartDate')" />
                計画期間開始日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.planStartDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.planStartDate.date" @change="onDateChange('planStartDate', $event)" :disabled="!subsidyStore.formData.dateJson.planStartDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.planStartDate?.userName">
                  {{ subsidyStore.getCheckInfo('planStartDate') }}
                </div>
              </div>
            </dd>
            <dt>
              <label>
                <input type="checkbox" v-model="subsidyStore.formData.dateJson.planEndDate.isChecked" @change="subsidyStore.updateCheckboxState('planEndDate')" />
                計画期間終了日
              </label>
            </dt>
            <dd>
              <BookmarkInput v-model="subsidyStore.formData.colorInfo.planEndDate" />
              <div>
                <input type="date" v-model="subsidyStore.formData.dateJson.planEndDate.date" @change="onDateChange('planEndDate', $event)" :disabled="!subsidyStore.formData.dateJson.planEndDate.isChecked" />
                <div class="date-user" v-if="subsidyStore.formData.dateJson.planEndDate?.userName">
                  {{ subsidyStore.getCheckInfo('planEndDate') }}
                </div>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  .date-user {
    color: #6c757d;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    text-align: right;
    position: absolute;
    margin-top: -5px;
  }
</style>
